import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { api, type CreatedKey, type Key } from '../api';
import { formatAgo, formatDate } from '../format';
import { CopyButton, DangerAction, Empty, PageHead, Pill, TableWrap } from '../ui';
import { useConfirmable } from '../useConfirmable';

export default function Keys() {
  const [keys, setKeys] = useState<Key[]>([]);
  const [name, setName] = useState('');
  const [allowList, setAllowList] = useState('');
  const [created, setCreated] = useState<CreatedKey | null>(null);
  const [showRevoked, setShowRevoked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const { run, dialog } = useConfirmable();

  const load = useCallback(async () => {
    try {
      setKeys((await api.keys()).items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load keys');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Revoked keys stay for the record and pile up; live ones are what the page
  // is about. Mixed together, a greyed-out row two places above the key you
  // meant to revoke is exactly the mis-click this separation removes.
  const active = useMemo(() => keys.filter((key) => !key.revoked), [keys]);
  const revoked = useMemo(() => keys.filter((key) => key.revoked), [keys]);

  const create = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const entries = allowList
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean);
      setCreated(await run('issue_key', (confirm) => api.createKey(name.trim(), entries, confirm)));
      setName('');
      setAllowList('');
      await load();
    } catch (err) {
      if (err instanceof Error && err.message === 'cancelled') return;
      setError(err instanceof Error ? err.message : 'Could not create the key');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: number) => {
    setError('');
    try {
      await run('revoke_key', (confirm) => api.revokeKey(id, confirm));
      await load();
    } catch (err) {
      if (err instanceof Error && err.message === 'cancelled') return;
      setError(err instanceof Error ? err.message : 'Could not revoke the key');
    }
  };

  return (
    <>
      {dialog}
      <PageHead
        title="Keys"
        lead="A key is the whole of your authentication. Issue one per integration, so revoking it stops only that one."
      />

      {created ? (
        <div className="panel">
          <div className="panel-head">
            <h3>{created.key.name || 'New key'}</h3>
            <Pill status="ACTIVE" />
          </div>
          <div className="snippet">
            <pre className="mono">{created.plaintext}</pre>
            <CopyButton text={created.plaintext} label="Copy key" className="secondary small" />
          </div>
          <div className="notice">
            This is the only time the key is shown. We keep a hash of it and nothing else, so a lost
            key is replaced rather than recovered.
          </div>
          <div className="row">
            <CopyButton text={created.plaintext} label="Copy key" />
            <button className="secondary" onClick={() => setCreated(null)}>
              Done
            </button>
          </div>
        </div>
      ) : null}

      {error ? <p className="error">{error}</p> : null}

      <div className="panel">
        <div className="panel-head">
          <h3>Active keys</h3>
          <span className="muted small">
            {active.length} live{revoked.length ? ` · ${revoked.length} revoked` : ''}
          </span>
        </div>
        {active.length === 0 ? (
          <Empty title="No keys yet.">The API refuses every request until there is one.</Empty>
        ) : (
          <TableWrap>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Key</th>
                  <th className="optional">Allowed from</th>
                  <th>Last used</th>
                  <th className="optional">Created</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {active.map((key) => (
                  <tr key={key.id}>
                    <td>{key.name || <span className="muted">unnamed</span>}</td>
                    <td className="mono small">{key.prefix}…</td>
                    <td className="small optional">
                      {key.ip_allowlist?.length ? (
                        key.ip_allowlist.join(', ')
                      ) : (
                        <span className="muted">any address</span>
                      )}
                    </td>
                    <td className="small">
                      {key.last_used_at ? formatAgo(key.last_used_at) : <span className="muted">never</span>}
                    </td>
                    <td className="small optional">{formatDate(key.created_at)}</td>
                    <td>
                      {/* Asks before it acts: revoking is immediate and a
                          production integration stops with it. */}
                      <DangerAction
                        label="Revoke"
                        confirmLabel="Revoke it"
                        question="Calls with this key stop at once."
                        onConfirm={() => void revoke(key.id)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </div>

      <div className="panel">
        <h3>New key</h3>
        <form className="row" onSubmit={create}>
          <div>
            <label htmlFor="key-name">Name</label>
            <input
              id="key-name"
              placeholder="production"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor="allow">Allowed addresses</label>
            <input
              id="allow"
              style={{ width: '100%' }}
              placeholder="empty means any — 203.0.113.4, 198.51.100.0/24"
              value={allowList}
              onChange={(event) => setAllowList(event.target.value)}
            />
          </div>
          <button type="submit" disabled={busy}>
            {busy ? 'Creating…' : 'Create key'}
          </button>
        </form>
        <p className="panel-note">
          An allowlist is optional. Leave it empty unless you know the addresses your servers call
          from — a list filled in with a guess locks you out at the worst moment.
        </p>
      </div>

      {revoked.length ? (
        <div className="panel">
          <div className="panel-head">
            <h3>Revoked</h3>
            <button className="secondary small" onClick={() => setShowRevoked((on) => !on)}>
              {showRevoked ? 'Hide' : `Show ${revoked.length}`}
            </button>
          </div>
          {showRevoked ? (
            <TableWrap>
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Key</th>
                    <th>Last used</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {revoked.map((key) => (
                    <tr key={key.id}>
                      <td className="muted">{key.name || 'unnamed'}</td>
                      <td className="mono small muted">{key.prefix}…</td>
                      <td className="small muted">{formatAgo(key.last_used_at, 'never')}</td>
                      <td>
                        <Pill status="revoked" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          ) : (
            <p className="panel-note">
              Kept so a key that appears in an old log can still be identified. They authenticate
              nothing.
            </p>
          )}
        </div>
      ) : null}
    </>
  );
}
